import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  Users, 
  Crown,
  Shield,
  Loader2,
  Search,
  Ban,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Mail,
  Calendar,
  MapPin,
  Clock,
  UserCheck,
  Key,
  Send,
  X,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date to dd/mm/yyyy hh:mm
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 20;
  
  // User detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Contact user modal
  const [showContactModal, setShowContactModal] = useState(false);

  // Discipline
  const [disciplineInfo, setDisciplineInfo] = useState(null);
  const [disciplineLoading, setDisciplineLoading] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token, page, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/users?limit=${pageSize}&skip=${(page - 1) * pageSize}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (roleFilter && roleFilter !== 'all') url += `&role=${roleFilter}`;
      if (statusFilter === 'banned') url += `&banned=true`;
      if (statusFilter === 'premium') url += `&premium=true`;
      if (statusFilter === 'verified') url += `&verified=true`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(res.data);
      if (res.data.length < pageSize) {
        setTotalUsers((page - 1) * pageSize + res.data.length);
      } else {
        setTotalUsers(page * pageSize + 1);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You do not have admin access');
        navigate('/');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleSetRole = async (userId, role) => {
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, 
        { role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User role updated to ${role}`);
      fetchUsers();
      setShowUserModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyEmail = async (userId) => {
    if (!window.confirm('Manually verify this user\'s email address?')) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/verify-email`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email verified successfully');
      fetchUsers();
      setShowUserModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify email');
    } finally {
      setActionLoading(false);
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (deleteEmailInput.trim().toLowerCase() !== selectedUser.email.toLowerCase()) {
      toast.error('Email does not match — please type the exact email address');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedUser.email} permanently deleted`);
      setShowUserModal(false);
      setShowDeleteConfirm(false);
      setDeleteEmailInput('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user? Their listings will also be hidden.')) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/ban`, 
        { reason: 'Banned by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User banned and listings hidden');
      fetchUsers();
      setShowUserModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to ban user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/unban`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User unbanned and listings restored');
      fetchUsers();
      setShowUserModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unban user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContactUser = async () => {
    if (!contactSubject.trim() || !contactMessage.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/contact`, 
        { subject: contactSubject, message: contactMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email sent successfully to ${selectedUser.email}`);
      setShowContactModal(false);
      setContactSubject('');
      setContactMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const openContactModal = (u) => {
    setSelectedUser(u);
    setContactSubject('');
    setContactMessage('');
    setShowContactModal(true);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'superadmin':
        return <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">SUPERADMIN</span>;
      case 'admin':
        return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">ADMIN</span>;
      case 'user_admin':
        return <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">USER ADMIN</span>;
      case 'premium_user':
        return <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">PREMIUM</span>;
      default:
        return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs">User</span>;
    }
  };

  const getAuthBadge = (authMethod) => {
    if (!authMethod) return null;
    switch (authMethod) {
      case 'google':
        return (
          <span className="px-2 py-0.5 rounded bg-red-50 text-red-600 text-[10px] flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/></svg>
            Google
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-500 text-[10px] flex items-center gap-1">
            <Mail className="w-3 h-3" />
            Email
          </span>
        );
    }
  };

  const getStatusBadge = (u) => {
    if (u.is_banned) {
      return <span className="flex items-center gap-1 text-red-600 text-xs"><Ban className="w-3 h-3" /> Banned</span>;
    }
    if (u.is_premium) {
      return <span className="flex items-center gap-1 text-[#C9956C] text-xs"><Crown className="w-3 h-3" /> Premium</span>;
    }
    if (u.is_verified) {
      return <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle className="w-3 h-3" /> Verified</span>;
    }
    return <span className="flex items-center gap-1 text-amber-600 text-xs"><AlertTriangle className="w-3 h-3" /> Unverified</span>;
  };

  const openUserDetail = (u) => {
    setSelectedUser(u);
    setShowUserModal(true);
    fetchDisciplineInfo(u.id);
  };

  const fetchDisciplineInfo = async (userId) => {
    setDisciplineLoading(true);
    setDisciplineInfo(null);
    try {
      const res = await fetch(`${API_URL}/admin/users/${userId}/discipline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDisciplineInfo(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch discipline info:', e);
    }
    setDisciplineLoading(false);
  };

  const handleAddStrike = async (userId, reason) => {
    if (!reason) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/users/${userId}/strikes?points=1&reason=${encodeURIComponent(reason)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Strike added');
        fetchDisciplineInfo(userId);
      } else {
        toast.error('Failed to add strike');
      }
    } catch (e) {
      toast.error('Failed to add strike');
    }
    setActionLoading(false);
  };

  const handleRemoveStrike = async (userId, strikeId) => {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/users/${userId}/strikes/${strikeId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Strike removed');
        fetchDisciplineInfo(userId);
      } else {
        toast.error('Failed to remove strike');
      }
    } catch (e) {
      toast.error('Failed to remove strike');
    }
    setActionLoading(false);
  };

  if (loading && users.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              User Management
            </h1>
            <p className="text-gray-500">
              View and manage all platform users
            </p>
          </div>
          <Button onClick={fetchUsers} variant="outline" className="gap-2" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by email or username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9 bg-white border-gray-300"
                />
              </div>
            </div>
            
            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="premium_user">Premium User</SelectItem>
                <SelectItem value="user_admin">User Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="superadmin">Superadmin</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="banned">Banned</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={handleSearch}>
              Search
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Auth</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Trust</th>
                  <th className="px-4 py-3 font-medium">Last Login</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr 
                    key={u.id} 
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openUserDetail(u)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                          {u.username?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.username}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getRoleBadge(u.role)}
                    </td>
                    <td className="px-4 py-3">
                      {getAuthBadge(u.auth_method)}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(u)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">{u.trust_score || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDateTime(u.last_login)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openUserDetail(u); }}
                        className="text-xs h-7"
                      >
                        <UserCog className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((page - 1) * pageSize) + 1} - {((page - 1) * pageSize) + users.length} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">Page {page}</span>
              <Button
                size="sm"
                variant="outline"
                disabled={users.length < pageSize}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              User Details
            </DialogTitle>
            <DialogDescription>
              View and manage user account
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-2xl font-bold">
                  {selectedUser.username?.[0]?.toUpperCase() || 'U'}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedUser.username}</h3>
                  <p className="text-gray-500 flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {selectedUser.email}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {getRoleBadge(selectedUser.role)}
                    {getAuthBadge(selectedUser.auth_method)}
                  </div>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  {getStatusBadge(selectedUser)}
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Trust Score</p>
                  <p className="font-medium">{selectedUser.trust_score || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Joined
                  </p>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last Login
                  </p>
                  <p className="font-medium">{formatDateTime(selectedUser.last_login)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Account Type</p>
                  <p className="font-medium capitalize">{selectedUser.account_type || 'both'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Location
                  </p>
                  <p className="font-medium">{selectedUser.location_name || 'Not set'}</p>
                </div>
              </div>
              
              {/* Actions */}
              {selectedUser.id !== user?.id && (
                <div className="pt-4 border-t space-y-3">
                  {/* Email Verification */}
                  {!selectedUser.is_verified && !selectedUser.is_banned && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Email Verification</p>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVerifyEmail(selectedUser.id)}
                        disabled={actionLoading}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        Manually Verify Email
                      </Button>
                    </div>
                  )}
                  
                  {/* Role Management (Superadmin only) */}
                  {user?.role === 'superadmin' && selectedUser.role !== 'superadmin' && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Change Role</p>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant={selectedUser.role === 'user' ? 'default' : 'outline'}
                          onClick={() => handleSetRole(selectedUser.id, 'user')}
                          disabled={actionLoading || selectedUser.role === 'user'}
                          className="text-xs"
                        >
                          User
                        </Button>
                        <Button 
                          size="sm" 
                          variant={selectedUser.role === 'premium_user' ? 'default' : 'outline'}
                          onClick={() => handleSetRole(selectedUser.id, 'premium_user')}
                          disabled={actionLoading || selectedUser.role === 'premium_user'}
                          className="text-xs"
                        >
                          Premium
                        </Button>
                        <Button 
                          size="sm" 
                          variant={selectedUser.role === 'user_admin' ? 'default' : 'outline'}
                          onClick={() => handleSetRole(selectedUser.id, 'user_admin')}
                          disabled={actionLoading || selectedUser.role === 'user_admin'}
                          className="text-xs"
                        >
                          User Admin
                        </Button>
                        <Button 
                          size="sm" 
                          variant={selectedUser.role === 'admin' ? 'default' : 'outline'}
                          onClick={() => handleSetRole(selectedUser.id, 'admin')}
                          disabled={actionLoading || selectedUser.role === 'admin'}
                          className="text-xs"
                        >
                          Admin
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Ban/Unban */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Account Status</p>
                    {selectedUser.is_banned ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUnbanUser(selectedUser.id)}
                        disabled={actionLoading}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Unban User
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleBanUser(selectedUser.id)}
                        disabled={actionLoading}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        Ban User
                      </Button>
                    )}
                  </div>
                  
                  {/* Discipline Info */}
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Discipline
                    </p>
                    {disciplineLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    ) : disciplineInfo ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${
                            disciplineInfo.strike_count >= 7 ? 'text-red-600' :
                            disciplineInfo.strike_count >= 3 ? 'text-orange-600' :
                            disciplineInfo.strike_count >= 1 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {disciplineInfo.strike_count || 0} strikes
                          </span>
                          {disciplineInfo.suspension_status && (
                            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                              {disciplineInfo.suspension_status.tier === 'permanent' ? 'Permanent Ban' :
                               `Suspended (${disciplineInfo.suspension_status.tier})`}
                            </span>
                          )}
                        </div>
                        {disciplineInfo.strike_history?.length > 0 && (
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {disciplineInfo.strike_history.slice().reverse().map((s) => (
                              <div key={s.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                                <div className="flex-1 min-w-0">
                                  <span className="text-gray-600 truncate block">{s.reason}</span>
                                  <span className="text-gray-400">{s.source} | +{s.points} | {new Date(s.created_at).toLocaleDateString('en-AU')}</span>
                                </div>
                                <button
                                  onClick={() => handleRemoveStrike(selectedUser.id, s.id)}
                                  disabled={actionLoading}
                                  className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
                                  title="Remove strike"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const reason = prompt('Reason for strike:');
                            if (reason) handleAddStrike(selectedUser.id, reason);
                          }}
                          disabled={actionLoading}
                          className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50"
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Add Strike
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No discipline data</p>
                    )}
                  </div>

                  {/* Contact User */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Communication</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowUserModal(false);
                        openContactModal(selectedUser);
                      }}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Contact User
                    </Button>
                  </div>

                  {/* Permanent Delete — superadmin only */}
                  {user?.role === 'superadmin' && (
                    <div className="pt-3 border-t border-red-100">
                      <p className="text-xs text-red-500 font-medium mb-2 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Danger Zone
                      </p>
                      {!showDeleteConfirm ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowDeleteConfirm(true); setDeleteEmailInput(''); }}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Permanently Delete Account
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-red-600">
                            This deletes <strong>all data</strong> for this account — listings, messages, offers, reviews, wallet — permanently. Type their email to confirm.
                          </p>
                          <Input
                            placeholder={selectedUser.email}
                            value={deleteEmailInput}
                            onChange={e => setDeleteEmailInput(e.target.value)}
                            className="text-sm border-red-200 focus:ring-red-400"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleDeleteUser}
                              disabled={deleteLoading || deleteEmailInput.trim().toLowerCase() !== selectedUser.email.toLowerCase()}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs"
                            >
                              {deleteLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                              Permanently Delete
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setShowDeleteConfirm(false); setDeleteEmailInput(''); }}
                              className="text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUserModal(false); setShowDeleteConfirm(false); setDeleteEmailInput(''); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact User Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Contact User
            </DialogTitle>
            <DialogDescription>
              Send an email to {selectedUser?.username} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
              <Input
                placeholder="Email subject..."
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Message</label>
              <textarea
                placeholder="Write your message here..."
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> This email will be sent from <span className="font-mono">support@wantd.au</span>. 
                The user can reply directly to this address.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleContactUser}
              disabled={sendingEmail || !contactSubject.trim() || !contactMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
